import { FC } from 'react';
import { Card, CardHeader, IconButton } from '@mui/material';
import {
  DragIndicatorOutlined as DragHandleIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useDraggable } from '@dnd-kit/core';
import type { Cut, Extra, Specification, Trim } from '../../types';

export interface DraggableCapabilityItemProps {
  item: Cut | Trim | Extra | Specification;
  type: 'cut' | 'trim' | 'extra' | 'specification';
  onEdit: () => void;
  isUsed?: boolean;
}

/**
 * A draggable card representing a single capability (cut, trim, extra, or
 * specification) in the left-hand palette.  The card stays in-place while
 * dragging — the floating visual is handled by DragOverlay.
 * @param root0
 * @param root0.item
 * @param root0.type
 * @param root0.onEdit
 * @param root0.isUsed
 */
export const DraggableCapabilityItem: FC<DraggableCapabilityItemProps> = ({
  item,
  type,
  onEdit,
  isUsed = false,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${type}-${item.id}`,
    data: {
      type: 'capability',
      capability: item,
      capabilityType: type,
    },
  });

  // Don't apply transform to the original card — DragOverlay handles the
  // floating visual. Keeping the card in place prevents it from being clipped
  // by the overflow:auto left panel when dragging across to the right panel.
  const style = {
    opacity: isDragging ? 0.3 : isUsed ? 0.7 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      elevation={0}
      sx={{
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <CardHeader
        avatar={
          <IconButton
            size="small"
            sx={{
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
              p: 0.25,
            }}
            {...listeners}
            {...attributes}
          >
            <DragHandleIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </IconButton>
        }
        action={
          <IconButton size="small" onClick={onEdit} sx={{ p: 0.25, pr: 1.5 }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        }
        title={item.title || 'Unnamed'}
        slotProps={{
          title: { variant: 'body2' },
          action: { sx: { alignSelf: 'center' } },
          avatar: { sx: { alignSelf: 'center' } },
        }}
        sx={{ py: 0.5, px: 1 }}
      />
    </Card>
  );
};
